import React, { useState, useRef, useEffect, useCallback } from 'react';

const ResizableHorizontal = ({
  initialWidth = 220,
  minWidth = 160,      // puede ser 160 o "20%"
  maxWidth = 420,      // puede ser 420 o "40%"
  collapsed = false,
  collapsedWidth = 60,
  children,
}) => {
  const [width, setWidth] = useState(initialWidth);
  const [expandedWidth, setExpandedWidth] = useState(initialWidth);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const isResizingRef = useRef(false);
  const wrapperRef = useRef(null);

  const clampWidth = useCallback(
    (value) => {
      const parent = wrapperRef.current
        ? wrapperRef.current.parentElement
        : null;
      const containerWidth = parent
        ? parent.getBoundingClientRect().width
        : 0;

      const resolveLimit = (limit, fallback) => {
        if (
          typeof limit === 'string' &&
          limit.trim().endsWith('%') &&
          containerWidth
        ) {
          const num = parseFloat(limit);
          if (!isNaN(num)) {
            return (num / 100) * containerWidth;
          }
        }

        if (typeof limit === 'number') {
          return limit;
        }

        return fallback;
      };

      const minPx = resolveLimit(minWidth, 0);
      const maxPx = resolveLimit(maxWidth, Number.POSITIVE_INFINITY);

      let next = value;
      if (next < minPx) next = minPx;
      if (next > maxPx) next = maxPx;
      return next;
    },
    [minWidth, maxWidth]
  );

  // Ajustar el ancho inicial por si min/max son porcentajes
  useEffect(() => {
    const parent = wrapperRef.current
      ? wrapperRef.current.parentElement
      : null;
    const containerWidth = parent
      ? parent.getBoundingClientRect().width
      : 0;

    let initialPx = initialWidth;

    if (
      typeof initialWidth === 'string' &&
      initialWidth.trim().endsWith('%') &&
      containerWidth
    ) {
      const num = parseFloat(initialWidth);
      if (!isNaN(num)) {
        initialPx = (num / 100) * containerWidth;
      }
    }

    setWidth((prev) => clampWidth(typeof prev === 'number' ? prev : initialPx));
  }, [initialWidth, clampWidth]);

  // Handle collapse/expand
  useEffect(() => {
    if (collapsed) {
      setExpandedWidth(width);
      setWidth(collapsedWidth);
    } else {
      setWidth(expandedWidth);
    }
  }, [collapsed]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingRef.current || collapsed) return;

      const delta = e.clientX - startXRef.current;
      const rawWidth = startWidthRef.current + delta;
      const newWidth = clampWidth(rawWidth);

      setWidth(newWidth);
      setExpandedWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [clampWidth]);

  const handleMouseDown = (e) => {
    if (collapsed) return;
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  };

  const child = React.Children.only(children);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        flex: `0 0 ${width}px`,
        width,
        height: '100%',
        display: width === 0 ? 'none' : 'flex',
      }}
    >
      {width > 0 && React.cloneElement(child, {
        width,
        style: {
          ...(child.props.style || {}),
          width: '100%',
          height: '100%',
        },
      })}

      {!collapsed && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 4,
            cursor: 'col-resize',
            userSelect: 'none',
            height: '100%',
            zIndex: 10,
          }}
        />
      )}
    </div>
  );
};

export default ResizableHorizontal;
